"use client";

import type { LeadFlowContactQuestion as LeadFlowContactQuestionContent } from "@homeinstead/schemas";

import { formatPartialPhoneNumber } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import { Link } from "@homeinstead/ui/components/Link";
import { useId } from "react";
import { useLocale } from "~www/components/LocaleProvider";
import { useAnswer } from "../../hooks/use-answers";
import { useLeadFlowStep } from "../LeadFlowStepContext";
import { FieldError } from "./FieldError";

type ContactFieldName =
  LeadFlowContactQuestionContent["fields"][number]["field"];

type TextContactFieldName = Exclude<ContactFieldName, "OptInSms">;

const TEXT_FIELD_CONFIG: Record<
  TextContactFieldName,
  { autoComplete: string; defaultLabel: string; type: string }
> = {
  email: { autoComplete: "email", defaultLabel: "Email", type: "email" },
  firstName: {
    autoComplete: "given-name",
    defaultLabel: "First name",
    type: "text",
  },
  lastName: {
    autoComplete: "family-name",
    defaultLabel: "Last name",
    type: "text",
  },
  phone: { autoComplete: "tel", defaultLabel: "Phone", type: "tel" },
};

type ContactField = {
  _key: string;
  field?: ContactFieldName | null;
  label?: string | null;
  placeholder?: string | null;
};

type ContactQuestionProps = {
  item: {
    fields?: ContactField[] | null;
  };
};

function ContactTextField({
  field,
  fieldName,
}: {
  field: ContactField;
  fieldName: TextContactFieldName;
}) {
  const [value, setValue] = useAnswer(fieldName);
  const { errors, isInert = false } = useLeadFlowStep();
  const inputId = useId();
  const errorId = useId();
  const config = TEXT_FIELD_CONFIG[fieldName];
  const label = field.label || config.defaultLabel;
  const fieldErrors = errors[fieldName];
  const hasError = Boolean(fieldErrors?.length);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (isInert) {
      return;
    }

    const nextValue = event.currentTarget.value;
    setValue(
      fieldName === "phone"
        ? formatPartialPhoneNumber({ franchisePhone: nextValue })
        : nextValue,
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="sr-only" htmlFor={inputId}>
        {label}
      </label>
      <Input
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        autoComplete={config.autoComplete}
        className={cn({ "border-red-500": hasError })}
        id={inputId}
        name={fieldName}
        onChange={handleChange}
        placeholder={field.placeholder || label}
        type={config.type}
        value={value ?? ""}
      />
      <FieldError id={errorId} messages={fieldErrors} />
    </div>
  );
}

function SmsConsentField() {
  const [isChecked, setIsChecked] = useAnswer("OptInSms");
  const { isInert = false } = useLeadFlowStep();
  const locale = useLocale();
  const consentId = useId();
  const privacyPolicyHref = `${locale.metadata.canonicalOrigin}/legal/privacy-policy`;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (isInert) {
      return;
    }

    setIsChecked(event.currentTarget.checked);
  }

  return (
    <div className="flex items-start gap-3 text-xs leading-relaxed">
      <input
        checked={Boolean(isChecked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-new-beginnings-base"
        id={consentId}
        name="OptInSms"
        onChange={handleChange}
        type="checkbox"
      />
      <span>
        <label className="cursor-pointer" htmlFor={consentId}>
          By checking this box, I agree to receive SMS text messages from Home
          Instead at the phone number provided. Message &amp; data rates may
          apply. Message frequency may vary based on service needs and SMS
          activity. Reply STOP to opt out of further messaging. For more
          details, including our SMS terms, please refer to our{" "}
        </label>
        <Link
          className="text-xs underline hover:no-underline"
          href={privacyPolicyHref}
          rel="noopener noreferrer"
          size="inline"
          target="_blank"
          variant="plain"
        >
          Privacy Policy
        </Link>
        .
      </span>
    </div>
  );
}

/**
 * The privacy disclaimer, when `showPrivacyDisclaimer` is set, renders under
 * the step's submit button in `Step`, as on the legacy contact step.
 */
function ContactQuestion({ item }: ContactQuestionProps) {
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="sr-only">Contact information</legend>
      {(item.fields ?? []).map((field) => {
        if (field.field === "OptInSms") {
          return <SmsConsentField key={field._key} />;
        }

        return field.field ? (
          <ContactTextField
            field={field}
            fieldName={field.field}
            key={field._key}
          />
        ) : null;
      })}
    </fieldset>
  );
}

export { ContactQuestion };
